import React, { FC, memo, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Tab,
  TabFields,
  TabsType,
  Accordion,
  NotificationBanner,
  ActionDropdown,
  Modal
} from '@cmhc-schl/hearth-ui';
import { Box, Stack, Typography, useMediaQuery } from '@mui/material';
import { isServer } from '@sitecore-jss/sitecore-jss/utils';
import HbtBreadcrumbComponent from 'Feature/V2/HbtBreadcrumb/components/HbtBreadcrumbComponent';
import { useAuthenticationContext } from 'Foundation/Authentication';
import Helmet from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { getPortfolioLoanStatusDisplayName } from '../../utils/loanStatusMapping';
import { getApplicationDetail, getLoansValidationDetails } from './api/RequestOverviewService';
import {
  ApplicationPortfolioDetailPayload,
  LoanValidationDetailsType,
  PoolPrecisionTypeCode,
  PortfolioLoanRecordStatusCode
} from './api/types';
import { ApplicationLoanCompositionDetails } from './components/ApplicationLoanCompositionDetails/ApplicationLoanCompositionDetails';
import { ApplicationOverviewDetails } from './components/ApplicationOverviewDetails/ApplicationOverviewDetails';
import { getPortfolioLoanStatusColor } from './components/ApplicationOverviewDetails/utils/requestOverviewUtils';
import DocumentsResourcesFlyout from './components/DocumentsResourcesFlyout';
import HbtPortfolioLoansInventoryTable from './components/HbtPortfolioLoansInventoryTable';
import PortfolioCard from './components/PortfolioCard';
import styles from './styles.module.scss';
import LenderDetails from './components/LenderDetails/LenderDetails';
import i18n from 'i18next';
import { LanguageShort } from '@hobt/constants';
import { getSearchParams } from 'Feature/V2/Shared_Portfolio/components/SearchFlyout/utils/getSearchParams';
import { setSearchParams } from 'Feature/V2/Shared_Portfolio/components/SearchFlyout/utils/setSearchParams';
import { PortfolioNotificationConfig } from './components/PortfolioCard/types';

const HbtPortfolioManagementRequestOveriew: FC = () => {
  const isServerSSR = isServer();
  const { t } = useTranslation('HbtPortfolioManagementRequestOverview');

  const urlParams = new URLSearchParams(!isServerSSR ? window.location.search : '');
  const applicationId = urlParams.get('appID') ?? '';
  const portfolioIds = urlParams.get('pfID') ?? '';
  const { dataType, searchQuery } = getSearchParams(location.search);
  const searchParams =
    dataType && searchQuery ? setSearchParams({ dataType, query: searchQuery }) : '';
  const shouldSkipSearchPageFromBreadcrumb =
    dataType === 'Application ID' || dataType === 'Portfolio ID' || !dataType;
  const skippedHrefs = shouldSkipSearchPageFromBreadcrumb
    ? [`/pages/portfolio-management/search?${searchParams}`]
    : [];

  const portfolioIdsArr = useMemo(() => {
    return portfolioIds?.split(',').map(Number);
  }, [portfolioIds]);
  const poolPrecisionTypeCode = urlParams.get('pptCode') ?? '';
  const isCancellation = poolPrecisionTypeCode === PoolPrecisionTypeCode.Cancellation;

  const [pmRequestOverviewTabList, setPmRequestOverviewTabList] = useState<TabFields[]>([]);
  const [activeTab, setActiveTab] = useState<number>(0);
  const [contextAnchorEl, setContextAnchorEl] = useState<HTMLElement | null>(null);
  const [contextTabIndex, setContextTabIndex] = useState<number | null>(null);
  const [contextOpen, setContextOpen] = useState(false);
  const [isSelectionLimitModalOpen, setIsSelectionLimitModalOpen] = useState(false);
  const [notificationBannerConfig, setNotificationBannerConfig] =
    useState<PortfolioNotificationConfig | null>(null);

  const showSelectionLimitModal = () => {
    setIsSelectionLimitModalOpen(true);
  };

  const closeSelectionLimitModal = () => {
    setIsSelectionLimitModalOpen(false);
  };

  const handleTabChange = useCallback((newValue: number) => {
    setActiveTab(newValue);
  }, []);

  const handleViewLoanDetailsActionRowClick = (newTab: TabFields) => {
    if (!setPmRequestOverviewTabList) return;
    const tabIndex = pmRequestOverviewTabList.findIndex(
      (tab) => tab.label.value === newTab.label.value
    );
    if (tabIndex !== -1) {
      // setting to +1 because 0 is the default tab
      setActiveTab(tabIndex + 1);
      return;
    }
    const updatedTabs = [...pmRequestOverviewTabList, newTab];
    if (updatedTabs.length > 10) {
      showSelectionLimitModal();
      return;
    }
    setPmRequestOverviewTabList(updatedTabs);
    setActiveTab(updatedTabs.length);
  };

  const handleMutipleLoanDetailsActionClick = (newTabs: TabFields[]) => {
    if (!setPmRequestOverviewTabList) return;
    const tabs = [...pmRequestOverviewTabList];
    newTabs.forEach((newTab) => {
      if (tabs.some((tab) => tab.label.value === newTab.label.value)) return;
      tabs.push(newTab);
    });
    if (tabs.length > 10) {
      showSelectionLimitModal();
      return;
    }
    // finding the index of the first item in newTabs in tabs
    const tabIndex = tabs.findIndex((tab) => tab.label.value === newTabs[0].label.value);
    if (tabIndex !== -1) {
      // setting to +1 because 0 is the default tab
      setActiveTab(tabIndex + 1);
    } else {
      setActiveTab(tabs.length);
    }
    setPmRequestOverviewTabList(tabs);
  };

  const handleCloseTab = (index: number) => {
    if (index === 0) return;
    let newActiveTabIndex = activeTab;
    if (newActiveTabIndex === index) {
      newActiveTabIndex = 0;
    } else if (newActiveTabIndex > index) {
      newActiveTabIndex--;
    }
    setPmRequestOverviewTabList((prevTabs) => prevTabs.filter((_, i) => i + 1 !== index));
    setActiveTab(newActiveTabIndex);
    setContextOpen(false);
  };

  const contextOptions = [
    {
      label: t('PI-Portfolio-Overview-Close-Tab'),
      value: 'close',
      onClick: () => {
        if (contextTabIndex !== null) handleCloseTab(contextTabIndex);
        setContextOpen(false);
      },
      customStyles:
        contextTabIndex === 0
          ? ({
              color: '#999',
              pointerformatAmountWithDollarEvents: 'none',
              cursor: 'not-allowed'
            } as React.CSSProperties)
          : undefined
    },
    {
      label: t('PI-Portfolio-Overview-Close-Other-Tabs'),
      value: 'closeOthers',
      onClick: () => {
        if (contextTabIndex !== null) {
          if (contextTabIndex === 0) {
            setPmRequestOverviewTabList([]);
            setActiveTab(0);
          } else {
            const tabListIndex = contextTabIndex - 1;
            setPmRequestOverviewTabList((prevTabs) => {
              const updatedTabs = [prevTabs[tabListIndex]];
              setActiveTab(1);
              return updatedTabs;
            });
          }
          setContextOpen(false);
        }
      }
    },
    {
      label: t('PI-Portfolio-Overview-Close-All-Tabs'),
      value: 'closeAll',
      onClick: () => {
        setPmRequestOverviewTabList([]);
        setActiveTab(0);
        setContextOpen(false);
      }
    }
  ];

  const handleContextMenu = (event: React.MouseEvent, tabIndex: number) => {
    event.preventDefault();
    setContextAnchorEl(event.currentTarget as HTMLElement);
    setContextTabIndex(tabIndex);
    setContextOpen(true);
  };

  const getPortfolioLoanStatusName = (code: string | undefined): string | undefined => {
    if (!code) return '';
    return Object.keys(PortfolioLoanRecordStatusCode).find(
      (key: string) =>
        PortfolioLoanRecordStatusCode[key as keyof typeof PortfolioLoanRecordStatusCode] === code
    );
  };

  const authenticationContext = useAuthenticationContext();

  const [requestOverviewData, setRequestOverviewData] =
    useState<ApplicationPortfolioDetailPayload>();
  const [isRequestOverviewLoading, setIsRequestOverviewLoading] = useState<boolean>(false);
  const [requestOverviewError, setRequestOverviewError] = useState<boolean>(false);

  const [loanSummaryData, setLoanSummaryData] = useState<LoanValidationDetailsType[]>([]);
  const [isLoanSummaryLoading, setIsLoanSummaryLoading] = useState<boolean>(false);
  const [loanSummaryError, setLoanSummaryError] = useState<string>('');

  const [openFlyout, setOpenFlyout] = React.useState<boolean>(false);
  const [openModifyRemarkFlyout, setModifyRemarkFlyout] = React.useState<boolean>(false);
  const defaultTab: TabFields = {
    label: { value: t('PI-Portfolio-Management-Request-Overview-Loans-Inventory') },
    content: (
      <HbtPortfolioLoansInventoryTable
        isCancellation={isCancellation}
        applicationID={applicationId}
        portfolioIds={portfolioIdsArr}
        handleViewLoanDetailsActionRowClick={handleViewLoanDetailsActionRowClick}
        handleMutipleLoanDetailsActionClick={handleMutipleLoanDetailsActionClick}
        openFlyout={openFlyout}
        setOpenFlyout={setOpenFlyout}
        openModifyRemarkFlyout={openModifyRemarkFlyout}
        setModifyRemarkFlyout={setModifyRemarkFlyout}
      />
    )
  };

  useEffect(() => {
    setIsRequestOverviewLoading(true);
    if (requestOverviewData && requestOverviewData?.insuredApprovedLenderCode !== null) {
      setRequestOverviewData(undefined);
    }
    getApplicationDetail({
      authenticationContext,
      applicationID: applicationId,
      portfolioIds
    })
      .then((data: ApplicationPortfolioDetailPayload | undefined) => {
        setIsRequestOverviewLoading(false);
        setRequestOverviewError(false);
        setRequestOverviewData(data);
        if (data?.insuredApprovedLenderCode === null) {
          setRequestOverviewError(true);
        }
      })
      .catch(() => {
        setRequestOverviewError(true);
        setIsRequestOverviewLoading(false);
        setRequestOverviewData(undefined);
        // eslint-disable-next-line no-console
        console.error('Request overview data error');
      });
  }, [applicationId, portfolioIds]);

  useEffect(() => {
    setIsLoanSummaryLoading(true);
    if (loanSummaryData.length > 0 && loanSummaryData[0]?.totalAmount !== null) {
      setLoanSummaryData([]);
    }

    getLoansValidationDetails({
      authenticationContext,
      applicationID: applicationId,
      portfolioIds
    })
      .then(
        (
          data = {
            totalAmount: 0,
            totalCount: 0,
            loanValidationDetails: []
          }
        ) => {
          const totalCount = data?.totalCount;
          const totalAmount = data?.totalAmount;
          if (!data?.loanValidationDetails) {
            data.loanValidationDetails = [];
          }
          if (data?.loanValidationDetails.length > 0) {
            data?.loanValidationDetails.push({
              loanStatus: 'Total',
              count: requestOverviewData?.portfolioNumberOfLoansSubmitted || totalCount,
              amount: totalAmount,
              totalAmount,
              countPercentage: data.loanValidationDetails?.reduce(
                (amountAcc, detail) => detail.countPercentage + amountAcc,
                0
              ),
              amountPercentage: data.loanValidationDetails?.reduce(
                (amountAcc, detail) => detail.amountPercentage + amountAcc,
                0
              ),
              loanStatusName: 'Total'
            });
          }

          setLoanSummaryData(
            data?.loanValidationDetails.map((item: LoanValidationDetailsType) => ({
              ...item,
              count: item?.count,
              amount: item?.amount,
              totalAmount,
              countPercentage: item?.countPercentage,
              amountPercentage: item?.amountPercentage,
              loanStatusName: getPortfolioLoanStatusDisplayName(
                getPortfolioLoanStatusName(item?.loanStatus),
                t
              ),
              color: getPortfolioLoanStatusColor(getPortfolioLoanStatusName(item?.loanStatus))
            })) || []
          );
          setIsLoanSummaryLoading(false);
          setLoanSummaryError('');

          if (
            data?.loanValidationDetails.length === 0 ||
            (isCancellation && data?.totalCount === 0) ||
            (!isCancellation && data?.totalAmount === null)
          ) {
            setLoanSummaryError('no_data_error');
          }
        }
      )
      .catch(() => {
        setLoanSummaryError('api_error');
        setIsLoanSummaryLoading(false);
        setLoanSummaryData([]);
        // eslint-disable-next-line no-console
        console.error('Loan summary data error');
      });
  }, [applicationId, poolPrecisionTypeCode]);

  const isMobile = useMediaQuery('(max-width: 768px)');
  const [isRequestDetailsCollapsed, setIsRequestDetailsCollapsed] = useState(false);
  const [isResultsCollapsed, setIsResultsCollapsed] = useState(false);

  return (
    <>
      <Helmet>
        <title>{t('title')}</title>
      </Helmet>
      <HbtBreadcrumbComponent
        {...t('breadcrumb', {
          returnObjects: true,
          searchParams,
          applicationNumber: applicationId
        })}
        skippedHrefs={skippedHrefs}
      />

      <Box className={styles.requestOverviewDetailsWrapper}>
        {notificationBannerConfig && (
          <div className={styles.notificationsContainer}>
            <NotificationBanner
              showNotificationBanner={!!notificationBannerConfig}
              onCloseNotificationBanner={() => setNotificationBannerConfig(null)}
              headerText={{
                value: t(notificationBannerConfig.header)
              }}
              message={{
                value: t(notificationBannerConfig.subHeader)
              }}
              variant={notificationBannerConfig.variant}
            />
          </div>
        )}
        <Box className={styles.applicationOverviewContainer}>
          <Stack direction="column" className={styles.titleWrapper} gap={4}>
            <Box
              className={styles.titleContainer}
              role="region"
              aria-labelledby="request-overview-heading"
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  mb: { xs: 2, md: 0 },
                  gap: '2rem',
                  alignSelf: 'flex-start'
                }}
              >
                <Typography
                  id="request-overview-heading"
                  variant="h1"
                  sx={{
                    fontSize: '1.5rem !important',
                    fontWeight: 'bold',
                    display: 'flex',
                    flexWrap: 'nowrap'
                  }}
                >
                  <span className="sr-only">:</span>
                  <span
                    tabIndex={0}
                    aria-label={t('PI-Portfolio-Management-Request-Overview-Application-ID')}
                  >
                    {t('PI-Portfolio-Management-Request-Overview-Application-ID')}&nbsp;
                    {applicationId}
                  </span>
                </Typography>
              </Box>

              <Box aria-labelledby="request-overview-heading">
                <DocumentsResourcesFlyout
                  fileId={requestOverviewData?.pirfFile?.fileId || ''}
                  fieldName={requestOverviewData?.pirfFile?.name || ''}
                  applicationId={Number(applicationId)}
                  aria-label={t('PI-Portfolio-Management-Request-Overview-Documents-Resources')}
                />
              </Box>
            </Box>

            {requestOverviewData && (
              <LenderDetails
                lenderCode={`${t('Insured-Approved-Lender-Code')}: ${requestOverviewData?.insuredApprovedLenderCode}`}
                lenderName={
                  i18n.language === LanguageShort.English
                    ? requestOverviewData?.insuredApprovedLenderName.englishName
                    : requestOverviewData?.insuredApprovedLenderName.frenchName
                }
              />
            )}
          </Stack>

          {/* Request Overview Container */}
          <Accordion
            accordionData={{
              heading: t('Application-Details'),
              defaultExpanded: true,
              contentSlot: (
                <Box className={styles.requestOverviewContainer}>
                  {/* LeftSide: Request Details */}

                  <ApplicationOverviewDetails
                    title={t('PI-Portfolio-Request-Overview-Application')}
                    showSpinner={isRequestOverviewLoading && !requestOverviewError}
                    showErrorMessage={!isRequestOverviewLoading && requestOverviewError}
                    className={`${
                      isRequestOverviewLoading || requestOverviewError
                        ? styles.requestDetailsErrorState
                        : ''
                    } ${isRequestDetailsCollapsed && isMobile ? styles.collapsed : ''}`}
                    data={requestOverviewData}
                    pptCode={poolPrecisionTypeCode}
                    isCancellationLoan={isCancellation}
                    termInsuranceToolTipBody={t(
                      'PI-Portfolio-Management-Request-Overview-Term-Insurance-Tooltip'
                    )}
                    totalLoansSubmittedToolTipBody={t(
                      'PI-Portfolio-Management-Request-Overview-Total-Loans-Submitted-Tooltip'
                    )}
                    options={{
                      insuredApprovedLenderCode: false,
                      insuredApprovedLenderName: false
                    }}
                  />

                  {/* RightSide: Validation Results */}

                  <ApplicationLoanCompositionDetails
                    title={t('Loan-Composition')}
                    showSpinner={isLoanSummaryLoading && loanSummaryError === ''}
                    showErrorMessage={!isLoanSummaryLoading && loanSummaryError !== ''}
                    className={`${
                      (isLoanSummaryLoading || loanSummaryError !== '') &&
                      styles.requestDetailsErrorState
                    } ${isResultsCollapsed && isMobile ? styles.collapsed : ''}`}
                    data={loanSummaryData}
                    isResultsCollapsed={isResultsCollapsed}
                    loanSummaryError={loanSummaryError}
                    isCancellationLoan={isCancellation}
                  />
                </Box>
              )
            }}
          ></Accordion>

          <Box className={styles.portfolioWrapper}>
            <PortfolioCard
              portfolioIds={portfolioIdsArr}
              onNotificationBanner={(config) => setNotificationBannerConfig(config)}
            />
          </Box>
          <Box className={styles.loansInventoryWrapper}>
            {/* Loans Inventory */}
            <Typography className={styles.header} variant="h1">
              <div className={styles.loanManagementHeading}>{t('Loan-Inventory')}</div>
            </Typography>
            <Tab
              tabs={[defaultTab, ...pmRequestOverviewTabList]}
              tabsType={TabsType.FULL_WIDTH}
              initialActiveTabIndex={activeTab}
              onTabChange={handleTabChange}
              onTabRightClick={handleContextMenu}
              showCloseButton={(_: unknown, index: number) => index}
              onTabClose={(index) => {
                handleCloseTab(index);
              }}
              tabWidthAuto
            />
            {contextAnchorEl && (
              <div className={styles.closeTabMenu}>
                <ActionDropdown
                  anchorEl={contextAnchorEl}
                  open={contextOpen}
                  onToggle={(open) => setContextOpen(open)}
                  options={contextOptions}
                  trigger={<></>}
                />
              </div>
            )}
          </Box>
        </Box>
        <Modal
          id={'selction-limit-reached'}
          isActive={isSelectionLimitModalOpen}
          onDismissalCallback={closeSelectionLimitModal}
          title={{ value: t('Selection-Limit-Reached') }}
          bodyContent={t('Selection-Limit-Body-Text')}
          shouldCloseOnBackgroundClick={true}
        />
      </Box>
    </>
  );
};

export default memo(HbtPortfolioManagementRequestOveriew);
